import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { Button, Chip, Skeleton, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

import type { ChatbotAiMode, ChatbotAvailability, ChatbotChannel } from '../../model/types';

export interface AiStatusLineProps {
  availability: ChatbotAvailability | undefined;
  isLoading: boolean;
  isError: boolean;
  /** The saved `AiAgentSettings.enabled`. */
  enabled: boolean;
  /** The saved effective `aiMode` + which fields are overridden, per channel (the page's own
   * `/effective?channel=` queries — AI card doc §7.1). */
  effectiveAiMode: Record<ChatbotChannel, ChatbotAiMode | undefined>;
  effectiveOverriddenFields: Record<ChatbotChannel, string[] | undefined>;
  canViewTenant: boolean;
  onChangeAiMode: (scope: ChatbotChannel | 'GENERAL') => void;
}

type StatusReason = 'platform' | 'tenant' | 'disabled' | 'aiModeOff';

/**
 * "Is the assistant answering?" (AI card doc §7.1) — one chip per channel, evaluated on **saved**
 * state (never the unsaved draft), first-failing-condition-wins.
 */
export function AiStatusLine({
  availability,
  isLoading,
  isError,
  enabled,
  effectiveAiMode,
  effectiveOverriddenFields,
  canViewTenant,
  onChangeAiMode,
}: AiStatusLineProps) {
  const { t } = useTranslation('chatbot');

  if (isLoading) {
    return (
      <Stack spacing={1}>
        <Typography variant="subtitle2">{t('ai.status.title')}</Typography>
        <Stack direction="row" spacing={1}>
          <Skeleton variant="rounded" width={160} height={24} />
          <Skeleton variant="rounded" width={160} height={24} />
        </Stack>
      </Stack>
    );
  }

  if (isError || !availability || availability.channels.length === 0) {
    return null;
  }

  const aiCapability = availability.aiCapability ?? { effective: true, lockedByPlatform: false };

  return (
    <Stack spacing={1}>
      <Typography variant="subtitle2">{t('ai.status.title')}</Typography>
      <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap alignItems="center">
        {availability.channels.map((channel) => {
          const channelLabel = t(`scope.${channel.channel.toLowerCase()}`);
          const aiMode = effectiveAiMode[channel.channel];

          let reason: StatusReason | null = null;
          if (!aiCapability.effective && aiCapability.lockedByPlatform) reason = 'platform';
          else if (!aiCapability.effective) reason = 'tenant';
          else if (!enabled) reason = 'disabled';
          else if (aiMode === 'OFF') reason = 'aiModeOff';

          if (!reason) {
            const modeLabel =
              aiMode === 'CLASSIFY_ONLY'
                ? t('ai.status.modes.CLASSIFY_ONLY')
                : t('ai.status.modes.ASSIST');
            return (
              <Chip
                key={channel.channel}
                size="small"
                color="success"
                label={t('ai.status.on', { channel: channelLabel, mode: modeLabel })}
              />
            );
          }

          const chip = (
            <Chip
              key={channel.channel}
              size="small"
              variant="outlined"
              color={reason === 'platform' ? 'default' : 'warning'}
              icon={reason === 'platform' ? <LockOutlinedIcon fontSize="small" /> : undefined}
              label={t('ai.status.off', {
                channel: channelLabel,
                reason: t(`ai.status.reasons.${reason}`),
              })}
            />
          );

          if (reason === 'tenant' && canViewTenant) {
            return (
              <Stack key={channel.channel} direction="row" spacing={0.5} alignItems="center">
                {chip}
                <Button size="small" component={RouterLink} to="/settings/tenant">
                  {t('locked.tenantLink')}
                </Button>
              </Stack>
            );
          }
          if (reason === 'aiModeOff') {
            const scope: ChatbotChannel | 'GENERAL' = (
              effectiveOverriddenFields[channel.channel] ?? []
            ).includes('aiMode')
              ? channel.channel
              : 'GENERAL';
            return (
              <Stack key={channel.channel} direction="row" spacing={0.5} alignItems="center">
                {chip}
                <Button size="small" onClick={() => onChangeAiMode(scope)}>
                  {t('ai.status.changeAiMode')}
                </Button>
              </Stack>
            );
          }
          return chip;
        })}
      </Stack>
      <Typography variant="caption" color="text.secondary">
        {t('ai.status.savedOnly')}
      </Typography>
    </Stack>
  );
}
